/**
 * DelegationStatusChip — visual chip for a fiscal-delegation status, reusable
 * across every capability that surfaces a delegated authorisation:
 *
 *   - cassetto_fiscale (AdE Cassetto delega — 4-year horizon, pending 24-48h
 *     after SPID signature while AdE processes the request)
 *   - peppol_gateway / aruba_pec (PEC-based opt-in flows that surface a
 *     pending state until the legal-rep clicks the confirmation PEC link)
 *   - any future BE↔vendor flow where a third-party authorisation must be
 *     observed BEFORE the connection becomes usable
 *
 * The chip is **status-only** — it never carries the vendor's identity nor
 * any action; render it alongside the connection title in
 * ``<ConnectedAccountRow>`` (via ``connection.metadata.delegation_status``)
 * or inline inside a wizard summary. Keep the consumer free to choose where
 * to add an "renew" / "refresh" action — semantics belong to the host.
 *
 * A11y: ``role="status"`` with ``aria-live="polite"`` for non-critical
 * states; ``role="alert"`` for the ``expired`` critical state so screen
 * readers announce it on mount. Icon is decorative (``aria-hidden``).
 */
import { Check, Clock4, ShieldAlert } from "lucide-react";
import type * as React from "react";

import { cn } from "../../lib/cn";

export type DelegationStatus = "pending" | "active" | "expired";

interface DelegationStatusPresentation {
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly classes: string;
  readonly liveRole: "status" | "alert";
}

const PRESENTATION: Readonly<Record<DelegationStatus, DelegationStatusPresentation>> = {
  pending: {
    label: "In attesa AdE",
    icon: <Clock4 className="h-3 w-3" aria-hidden />,
    // Amber/warm — matches the Ando ``warning`` token (used by the
    // ``needs_reauth`` row variant so the visual language is consistent).
    classes: "border-warning/30 bg-warning/[0.08] text-warning",
    liveRole: "status",
  },
  active: {
    label: "Attiva",
    icon: <Check className="h-3 w-3" aria-hidden />,
    classes: "border-success/30 bg-success/[0.08] text-success",
    liveRole: "status",
  },
  expired: {
    label: "Scaduta",
    icon: <ShieldAlert className="h-3 w-3" aria-hidden />,
    classes: "border-error/30 bg-error/[0.08] text-error",
    liveRole: "alert",
  },
};

export interface DelegationStatusChipProps {
  readonly status: DelegationStatus;
  readonly className?: string;
}

export const DelegationStatusChip: React.FC<DelegationStatusChipProps> = ({
  status,
  className,
}) => {
  const p = PRESENTATION[status];
  return (
    <span
      role={p.liveRole}
      aria-live={p.liveRole === "alert" ? "assertive" : "polite"}
      aria-label={`Stato delega: ${p.label}`}
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1 rounded-full border px-2 font-medium text-[11px]",
        p.classes,
        className,
      )}
    >
      {p.icon}
      {p.label}
    </span>
  );
};

DelegationStatusChip.displayName = "DelegationStatusChip";

/** Narrow an arbitrary string into ``DelegationStatus`` or return ``null``. */
export function asDelegationStatus(value: unknown): DelegationStatus | null {
  return value === "pending" || value === "active" || value === "expired" ? value : null;
}
